import React, { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasLogisticsAccess } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Warning, Pencil, Trash } from "@phosphor-icons/react";

const CATEGORIES = ["Medical Equipment", "Medical Supplies", "Consumables", "Operational"];

export default function Inventory() {
  const { user } = useAuth();
  const canEdit = hasLogisticsAccess(user);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", category: "Medical Supplies", quantity: 0, threshold: 5, location: "Supply Room", notes: "" });
  const [filter, setFilter] = useState("");

  async function load() {
    const [i, l] = await Promise.all([api.get("/inventory/"), api.get("/inventory/locations")]);
    setItems(i.data); setLocations(l.data);
  }
  useEffect(() => { load(); }, []);

  function openCreate() { setEditing(null); setForm({ name: "", category: "Medical Supplies", quantity: 0, threshold: 5, location: locations[0] || "Supply Room", notes: "" }); setOpen(true); }
  function openEdit(it) { setEditing(it); setForm({ name: it.name, category: it.category, quantity: it.quantity, threshold: it.threshold, location: it.location, notes: it.notes || "" }); setOpen(true); }

  async function save(e) {
    e.preventDefault();
    try {
      if (editing) { await api.patch(`/inventory/${editing.id}`, form); toast.success("Updated"); }
      else { await api.post("/inventory/", form); toast.success("Item added"); }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }
  async function remove(it) {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    await api.delete(`/inventory/${it.id}`); toast.success("Deleted"); load();
  }

  const filtered = items.filter((i) => !filter || i.name.toLowerCase().includes(filter.toLowerCase()) || i.category.toLowerCase().includes(filter.toLowerCase()) || i.location.toLowerCase().includes(filter.toLowerCase()));
  const lowStock = items.filter((i) => i.quantity <= i.threshold).length;

  return (
    <AppLayout title="Inventory" subtitle="Equipment, supplies and consumables tracking" actions={
      <div className="flex items-center gap-2">
        <Input placeholder="Search items, category, location…" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-sm w-64" data-testid="inv-search" />
        {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openCreate} className="rounded-sm bg-slate-900 text-white" data-testid="inv-add"><Plus size={14} weight="bold" /> Add Item</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? "Edit Item" : "Add Inventory Item"}</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div><Label className="tab-tag">Name</Label><Input data-testid="inv-name" className="rounded-sm" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="tab-tag">Category</Label><select data-testid="inv-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></div>
                  <div><Label className="tab-tag">Location</Label><select data-testid="inv-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="w-full border border-slate-200 rounded-sm h-10 px-2 text-sm">{locations.map((l) => <option key={l}>{l}</option>)}</select></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="tab-tag">Quantity</Label><Input data-testid="inv-qty" type="number" min="0" className="rounded-sm" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: +e.target.value })} /></div>
                  <div><Label className="tab-tag">Low-stock threshold</Label><Input data-testid="inv-threshold" type="number" min="0" className="rounded-sm" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: +e.target.value })} /></div>
                </div>
                <DialogFooter><Button type="submit" className="rounded-sm bg-slate-900 text-white" data-testid="inv-save">Save</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>
    }>
      {lowStock > 0 && (
        <div className="surface-grid border-red-300 bg-red-50 px-4 py-2 mb-4 flex items-center gap-2" data-testid="low-stock-banner">
          <span className="inline-flex h-2 w-2 bg-red-600 rounded-full pulse-dot" />
          <Warning size={16} weight="bold" className="text-red-700" />
          <span className="text-sm text-red-700 font-medium">{lowStock} item(s) at or below low-stock threshold — submit a supply request.</span>
        </div>
      )}
      <div className="surface-grid">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Item</th><th className="px-4 py-2">Category</th><th className="px-4 py-2">Location</th><th className="px-4 py-2 text-right">Qty</th><th className="px-4 py-2 text-right">Threshold</th><th className="px-4 py-2">Status</th>{canEdit && <th className="px-4 py-2">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (<tr><td colSpan={7} className="px-4 py-6 text-slate-500">No items.</td></tr>) :
              filtered.map((it) => {
                const low = it.quantity <= it.threshold;
                return (
                  <tr key={it.id} className={`border-b border-slate-100 ${low ? "bg-red-50" : "hover:bg-slate-50"}`} data-testid={`inv-row-${it.id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{it.name}</td>
                    <td className="px-4 py-2"><Badge className="rounded-sm bg-slate-700 text-white">{it.category}</Badge></td>
                    <td className="px-4 py-2"><Badge className="rounded-sm bg-blue-600 text-white">{it.location}</Badge></td>
                    <td className={`px-4 py-2 text-right mono font-semibold ${low ? "text-red-700" : ""}`}>{it.quantity}</td>
                    <td className="px-4 py-2 text-right mono">{it.threshold}</td>
                    <td className="px-4 py-2 tab-tag">{low ? <span className="text-red-700">LOW STOCK</span> : <span className="text-emerald-700">OK</span>}</td>
                    {canEdit && (
                      <td className="px-4 py-2 flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(it)} data-testid={`inv-edit-${it.id}`}><Pencil size={14} /></Button>
                        <Button size="icon" variant="ghost" className="text-red-600" onClick={() => remove(it)} data-testid={`inv-delete-${it.id}`}><Trash size={14} /></Button>
                      </td>
                    )}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
